import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@features/auth/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { subDays } from "date-fns";
import {
  Users,
  Package,
  AlertCircle,
  Clock,
  CheckCircle,
  DollarSign,
  FileText,
  Activity,
  UserCheck,
  RefreshCw,
  Truck,
  Receipt,
} from "lucide-react";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@shared/components/ui/card";
import { Badge } from "@shared/components/ui/badge";
import { Button } from "@shared/components/ui/button";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@shared/components/ui/tabs";
import ThreeDotLoader from "@shared/components/ThreeDotLoader";
import useTitle from "@shared/hooks/useTitle";
import { DatePickerWithRange } from "@shared/components/date-picker-with-range";

import {
  useAssignedClientsQuery,
  useAssignedClientsPackages,
  useAssignedClientsTickets,
  useAssignedClientsInvoices,
  useAssignedClientsPickups,
  useAssignedClientsReturnVouchers,
} from "@features/client-support/hooks/useClientSupport";
import { useOverview } from "@features/dashboard/hooks/useStatstics";

export default function ClientSupportDashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("overview");

  useTitle(t("Client Support Dashboard"));

  // Use the authenticated user's ID (CLIENT SUPPORT can only view their own data)
  const supportUserId = user?._id || user?.id;

  // Date range state
  const [dateRange, setDateRange] = useState(() => {
    const now = new Date();
    const thirtyDaysAgo = subDays(now, 30);
    return { from: thirtyDaysAgo, to: now };
  });

  // Fetch assigned clients
  const {
    data: assignedClientsData,
    isLoading: clientsLoading,
    refetch: refetchClients,
  } = useAssignedClientsQuery(supportUserId, {
    page: 1,
    limit: 100,
  });

  // Fetch packages for assigned clients
  const {
    data: packagesData,
    isLoading: packagesLoading,
    refetch: refetchPackages,
  } = useAssignedClientsPackages({
    page: 1,
    limit: 10,
  });

  // Fetch tickets for assigned clients
  const {
    data: ticketsData,
    isLoading: ticketsLoading,
    refetch: refetchTickets,
  } = useAssignedClientsTickets({
    page: 1,
    limit: 10,
  });

  // Fetch invoices for assigned clients
  const {
    data: invoicesData,
    isLoading: invoicesLoading,
    refetch: refetchInvoices,
  } = useAssignedClientsInvoices({
    page: 1,
    limit: 10,
  });

  // Fetch pickups for assigned clients
  const {
    data: pickupsData,
    isLoading: pickupsLoading,
    refetch: refetchPickups,
  } = useAssignedClientsPickups({
    page: 1,
    limit: 10,
  });

  // Fetch return vouchers for assigned clients
  const {
    data: returnVouchersData,
    isLoading: returnVouchersLoading,
    refetch: refetchReturnVouchers,
  } = useAssignedClientsReturnVouchers({
    page: 1,
    limit: 10,
  });

  const assignedClients = assignedClientsData?.data || [];
  const packages = packagesData?.data || [];
  const tickets = ticketsData?.data || [];
  const invoices = invoicesData?.data || [];
  const pickups = pickupsData?.pickups || [];
  const returnVouchers = returnVouchersData || [];

  // Fetch dashboard overview statistics (includes recent packages with proper filtering)
  const overviewParams = useMemo(() => {
    return {
      from: dateRange.from,
      to: dateRange.to,
    };
  }, [dateRange]);

  const {
    data: overviewData,
    isLoading: overviewLoading,
    refetch: refetchOverview,
  } = useOverview(overviewParams);

  // Handle date range change
  const handleDateRangeChange = (newDateRange) => {
    setDateRange(newDateRange);
  };

  // Use recent packages from overview (these are properly filtered by backend for Client Support)
  const recentPackages =
    overviewData?.lastPackages || overviewData?.recentPackages || [];

  // Get package status breakdown from overview (properly filtered by backend)
  const packageStatusData = overviewData?.packageStatus || [];

  // Calculate statistics
  const stats = {
    totalClients: assignedClients.length,
    activeClients: assignedClients.filter((c) => c.isActive).length,
    totalPackages: overviewData?.packages?.totalPackagesInRange || 0,
    deliveredPackages:
      overviewData?.packages?.totalDelivered ||
      packages.filter((p) => p.status === "signed").length,
    pendingPackages: packages.filter((p) => p.status === "pending").length,
    packageStatus: packageStatusData, // Status breakdown from overview
    totalTickets: ticketsData?.totalTickets || 0,
    openTickets: tickets.filter((t) => t.status === "open").length,
    resolvedTickets: tickets.filter((t) => t.status === "resolved").length,
    totalInvoices: invoicesData?.totalInvoices || 0,
    paidInvoices: invoices.filter((i) => i.status === "paid").length,
    pendingInvoices: invoices.filter((i) => i.status === "pending").length,
    totalPickups:
      overviewData?.pickups?.totalPickups || pickupsData?.totalPickups || 0,
    pendingPickups:
      overviewData?.pickups?.totalPending ||
      pickups.filter((p) =>
        ["order_received", "order_confirmed"].includes(p.status)
      ).length,
    completedPickups:
      overviewData?.pickups?.totalCompleted ||
      pickups.filter((p) =>
        ["arrived_at_warehouse", "signed"].includes(p.status)
      ).length,
    totalReturnVouchers:
      overviewData?.returnVouchers?.totalReturnVouchers ||
      returnVouchers.length,
    pendingReturnVouchers:
      overviewData?.returnVouchers?.totalPending ||
      returnVouchers.filter((rv) => rv.status === "pending").length,
    paidReturnVouchers:
      overviewData?.returnVouchers?.totalPaid ||
      returnVouchers.filter((rv) => rv.status === "paid").length,
  };

  const handleRefreshAll = async () => {
    await Promise.all([
      refetchClients(),
      refetchPackages(),
      refetchTickets(),
      refetchInvoices(),
      refetchPickups(),
      refetchReturnVouchers(),
      refetchOverview(),
    ]);
  };

  const isLoading =
    clientsLoading ||
    packagesLoading ||
    ticketsLoading ||
    invoicesLoading ||
    pickupsLoading ||
    returnVouchersLoading;

  if (clientsLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <ThreeDotLoader />
      </div>
    );
  }

  // No assigned clients message
  if (assignedClients.length === 0) {
    return (
      <div className="space-y-6 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              {t("Client Support Dashboard")}
            </h1>
            <p className="text-muted-foreground">
              {t("Welcome to your client support dashboard")}
            </p>
          </div>
          <Badge variant="secondary" className="text-sm">
            <UserCheck className="h-4 w-4 mr-1" />
            {t("Client Support")}
          </Badge>
        </div>

        <Card className="bg-yellow-50 dark:bg-yellow-950/20 border-yellow-200 dark:border-yellow-800">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <AlertCircle className="h-6 w-6 text-yellow-600 dark:text-yellow-500 mt-1" />
              <div>
                <h3 className="text-lg font-semibold text-yellow-900 dark:text-yellow-100 mb-2">
                  {t("No Clients Assigned")}
                </h3>
                <p className="text-yellow-700 dark:text-yellow-300 mb-4">
                  {t(
                    "You don't have any clients assigned to you yet. Please contact your administrator or manager to get clients assigned."
                  )}
                </p>
                <Button
                  onClick={handleRefreshAll}
                  variant="outline"
                  className="border-yellow-300 text-yellow-700 hover:bg-yellow-50"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  {t("Refresh")}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              {t("Client Support Dashboard")}
            </h1>
            <p className="text-muted-foreground">
              {t("Manage and monitor your assigned clients")}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="text-sm">
              <UserCheck className="h-4 w-4 mr-1" />
              {t("Client Support")}
            </Badge>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefreshAll}
              disabled={isLoading}
            >
              <RefreshCw
                className={`h-4 w-4 mr-2 ${isLoading ? "animate-spin" : ""}`}
              />
              {t("Refresh")}
            </Button>
          </div>
        </div>

        {/* Date Range Filter */}
        <div className="flex items-center gap-4">
          <div className="flex-1 max-w-sm">
            <DatePickerWithRange
              initialDate={dateRange}
              onSelectDate={handleDateRangeChange}
            />
          </div>
          {overviewLoading && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <RefreshCw className="h-4 w-4 animate-spin" />
              {t("Loading statistics...")}
            </div>
          )}
        </div>
      </div>

      {/* Welcome Card */}
      <Card className="bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-950 dark:to-blue-900 border-blue-200 dark:border-blue-800">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-blue-900 dark:text-blue-100">
                {t("Welcome back")}, {user?.fullName || user?.name}!
              </h2>
              <p className="text-blue-700 dark:text-blue-300 mt-1">
                {t("You are currently managing")}{" "}
                <span className="font-semibold">{stats.totalClients}</span>{" "}
                {t("client(s)")} {t("with")}{" "}
                <span className="font-semibold">{stats.totalPackages}</span>{" "}
                {t("total packages")}
              </p>
            </div>
            <Button
              onClick={() => navigate("/packages")}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Package className="h-4 w-4 mr-2" />
              {t("View Packages")}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Key Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Assigned Clients */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  {t("Assigned Clients")}
                </p>
                <p className="text-2xl font-bold">{stats.totalClients}</p>
              </div>
              <div className="p-2 bg-purple-100 rounded-lg">
                <Users className="h-6 w-6 text-purple-600" />
              </div>
            </div>
            <div className="flex items-center mt-2 text-sm">
              <CheckCircle className="h-4 w-4 text-green-500 mr-1" />
              <span className="text-green-600">
                {stats.activeClients} {t("active")}
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Total Packages */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  {t("Total Packages")}
                </p>
                <p className="text-2xl font-bold">{stats.totalPackages}</p>
              </div>
              <div className="p-2 bg-blue-100 rounded-lg">
                <Package className="h-6 w-6 text-blue-600" />
              </div>
            </div>
            <div className="flex items-center mt-2 text-sm">
              <CheckCircle className="h-4 w-4 text-green-500 mr-1" />
              <span className="text-green-600">
                {stats.deliveredPackages} {t("delivered")}
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Total Pickups */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  {t("Total Pickups")}
                </p>
                <p className="text-2xl font-bold">{stats.totalPickups}</p>
              </div>
              <div className="p-2 bg-indigo-100 rounded-lg">
                <Truck className="h-6 w-6 text-indigo-600" />
              </div>
            </div>
            <div className="flex items-center mt-2 text-sm">
              <CheckCircle className="h-4 w-4 text-green-500 mr-1" />
              <span className="text-green-600">
                {stats.completedPickups} {t("completed")}
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Total Tickets */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  {t("Total Tickets")}
                </p>
                <p className="text-2xl font-bold">{stats.totalTickets}</p>
              </div>
              <div className="p-2 bg-orange-100 rounded-lg">
                <FileText className="h-6 w-6 text-orange-600" />
              </div>
            </div>
            <div className="flex items-center mt-2 text-sm">
              <Clock className="h-4 w-4 text-orange-500 mr-1" />
              <span className="text-orange-600">
                {stats.openTickets} {t("open")}
              </span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Secondary Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Total Invoices */}
        {/* <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  {t("Total Invoices")}
                </p>
                <p className="text-2xl font-bold">{stats.totalInvoices}</p>
              </div>
              <div className="p-2 bg-green-100 rounded-lg">
                <DollarSign className="h-6 w-6 text-green-600" />
              </div>
            </div>
            <div className="flex items-center mt-2 text-sm">
              <CheckCircle className="h-4 w-4 text-green-500 mr-1" />
              <span className="text-green-600">
                {stats.paidInvoices} {t("paid")}
              </span>
            </div>
          </CardContent>
        </Card> */}
      </div>

      {/* Management Tabs */}
      <Tabs
        value={activeTab}
        onValueChange={setActiveTab}
        className="space-y-4"
      >
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="overview">{t("Overview")}</TabsTrigger>
          <TabsTrigger value="clients">{t("My Clients")}</TabsTrigger>
          <TabsTrigger value="packages">{t("Packages")}</TabsTrigger>
          <TabsTrigger value="pickups">{t("Pickups")}</TabsTrigger>
          <TabsTrigger value="returnVouchers">
            {t("Return Vouchers")}
          </TabsTrigger>
          <TabsTrigger value="tickets">{t("Tickets")}</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Package Status Summary */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Package className="h-5 w-5" />
                  {t("Package Status Overview")}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {overviewLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <ThreeDotLoader />
                  </div>
                ) : stats.packageStatus && stats.packageStatus.length > 0 ? (
                  <>
                    {/* Status Breakdown */}
                    {stats.packageStatus.map((status) => {
                      // Status color mapping
                      const statusColors = {
                        order_received: "bg-blue-500",
                        picked_up: "bg-indigo-500",
                        in_transit: "bg-purple-500",
                        to_deliver: "bg-orange-500",
                        delivered: "bg-green-500",
                        signed: "bg-teal-500",
                        returned: "bg-red-500",
                        cancelled: "bg-gray-500",
                      };

                      const statusLabels = {
                        order_received: t("Order Received"),
                        picked_up: t("Picked Up"),
                        in_transit: t("In Transit"),
                        to_deliver: t("To Deliver"),
                        delivered: t("Delivered"),
                        signed: t("Signed"),
                        returned: t("Returned"),
                        cancelled: t("Cancelled"),
                      };

                      return (
                        <div
                          key={status._id}
                          className="flex items-center justify-between p-3 bg-muted rounded-lg hover:bg-muted/80 transition-colors"
                        >
                          <div className="flex items-center gap-2">
                            <div
                              className={`w-2 h-2 rounded-full ${
                                statusColors[status._id] || "bg-gray-500"
                              }`}
                            ></div>
                            <span className="text-sm font-medium">
                              {statusLabels[status._id] || status._id}
                            </span>
                          </div>
                          <Badge variant="secondary">{status.count}</Badge>
                        </div>
                      );
                    })}

                    {/* Total Summary */}
                    <div className="pt-3 border-t border-border">
                      <div className="flex items-center justify-between p-3 bg-primary/5 rounded-lg">
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full bg-primary"></div>
                          <span className="text-sm font-bold">
                            {t("Total Packages")}
                          </span>
                        </div>
                        <Badge variant="default">{stats.totalPackages}</Badge>
                      </div>
                    </div>

                    <Button className="w-full mt-2" asChild>
                      <a href="/packages">{t("View All Packages")}</a>
                    </Button>
                  </>
                ) : (
                  <>
                    {/* Fallback to basic stats if no status data */}
                    <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                        <span className="text-sm font-medium">
                          {t("Pending")}
                        </span>
                      </div>
                      <Badge variant="secondary">{stats.pendingPackages}</Badge>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-green-500"></div>
                        <span className="text-sm font-medium">
                          {t("Delivered")}
                        </span>
                      </div>
                      <Badge variant="default">{stats.deliveredPackages}</Badge>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-purple-500"></div>
                        <span className="text-sm font-medium">
                          {t("Total")}
                        </span>
                      </div>
                      <Badge variant="outline">{stats.totalPackages}</Badge>
                    </div>
                    <Button className="w-full" asChild>
                      <a href="/packages">{t("View All Packages")}</a>
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Ticket Status Summary */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  {t("Ticket Status Overview")}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-orange-500"></div>
                    <span className="text-sm font-medium">{t("Open")}</span>
                  </div>
                  <Badge variant="secondary">{stats.openTickets}</Badge>
                </div>
                <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-green-500"></div>
                    <span className="text-sm font-medium">{t("Resolved")}</span>
                  </div>
                  <Badge variant="default">{stats.resolvedTickets}</Badge>
                </div>
                <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-purple-500"></div>
                    <span className="text-sm font-medium">{t("Total")}</span>
                  </div>
                  <Badge variant="outline">{stats.totalTickets}</Badge>
                </div>
                <Button className="w-full" asChild>
                  <a href="/tickets">{t("View All Tickets")}</a>
                </Button>
              </CardContent>
            </Card>

            {/* Invoice Status Summary */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5" />
                  {t("Invoice Status Overview")}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-green-500"></div>
                    <span className="text-sm font-medium">{t("Paid")}</span>
                  </div>
                  <Badge variant="default">{stats.paidInvoices}</Badge>
                </div>
                <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
                    <span className="text-sm font-medium">{t("Pending")}</span>
                  </div>
                  <Badge variant="secondary">{stats.pendingInvoices}</Badge>
                </div>
                <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-purple-500"></div>
                    <span className="text-sm font-medium">{t("Total")}</span>
                  </div>
                  <Badge variant="outline">{stats.totalInvoices}</Badge>
                </div>
                <Button className="w-full" asChild>
                  <a href="/invoices">{t("View All Invoices")}</a>
                </Button>
              </CardContent>
            </Card>

            {/* Pickup Status Summary */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Truck className="h-5 w-5" />
                  {t("Pickup Status Overview")}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-green-500"></div>
                    <span className="text-sm font-medium">
                      {t("Completed")}
                    </span>
                  </div>
                  <Badge variant="default">{stats.completedPickups}</Badge>
                </div>
                <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-orange-500"></div>
                    <span className="text-sm font-medium">{t("Pending")}</span>
                  </div>
                  <Badge variant="secondary">{stats.pendingPickups}</Badge>
                </div>
                <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-purple-500"></div>
                    <span className="text-sm font-medium">{t("Total")}</span>
                  </div>
                  <Badge variant="outline">{stats.totalPickups}</Badge>
                </div>
                <Button className="w-full" asChild>
                  <a href="/pickups">{t("View All Pickups")}</a>
                </Button>
              </CardContent>
            </Card>

            {/* Return Voucher Status Summary */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Receipt className="h-5 w-5" />
                  {t("Return Voucher Status Overview")}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-green-500"></div>
                    <span className="text-sm font-medium">{t("Paid")}</span>
                  </div>
                  <Badge variant="default">{stats.paidReturnVouchers}</Badge>
                </div>
                <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
                    <span className="text-sm font-medium">{t("Pending")}</span>
                  </div>
                  <Badge variant="secondary">
                    {stats.pendingReturnVouchers}
                  </Badge>
                </div>
                <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-purple-500"></div>
                    <span className="text-sm font-medium">{t("Total")}</span>
                  </div>
                  <Badge variant="outline">{stats.totalReturnVouchers}</Badge>
                </div>
                <Button className="w-full" asChild>
                  <a href="/return-voucher">{t("View All Return Vouchers")}</a>
                </Button>
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="h-5 w-5" />
                  {t("Quick Actions")}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button className="w-full" variant="outline" asChild>
                  <a href="/packages">
                    <Package className="h-4 w-4 mr-2" />
                    {t("View Packages")}
                  </a>
                </Button>
                <Button className="w-full" variant="outline" asChild>
                  <a href="/pickups">
                    <Truck className="h-4 w-4 mr-2" />
                    {t("View Pickups")}
                  </a>
                </Button>
                <Button className="w-full" variant="outline" asChild>
                  <a href="/return-voucher">
                    <Receipt className="h-4 w-4 mr-2" />
                    {t("View Return Vouchers")}
                  </a>
                </Button>
                <Button className="w-full" variant="outline" asChild>
                  <a href="/tickets">
                    <FileText className="h-4 w-4 mr-2" />
                    {t("View Tickets")}
                  </a>
                </Button>
                <Button className="w-full" variant="outline" asChild>
                  <a href="/invoices">
                    <DollarSign className="h-4 w-4 mr-2" />
                    {t("View Invoices")}
                  </a>
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Clients Tab */}
        <TabsContent value="clients" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                {t("My Assigned Clients")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {assignedClients.length > 0 ? (
                <div className="space-y-3">
                  {assignedClients.map((client) => (
                    <Card
                      key={client._id}
                      className="border-l-4 border-l-blue-500"
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-blue-100 rounded-lg">
                              <Users className="h-5 w-5 text-blue-600" />
                            </div>
                            <div>
                              <h4 className="font-semibold">
                                {client.fullName}
                              </h4>
                              <p className="text-sm text-muted-foreground">
                                {client.email}
                              </p>
                              {client.brandName && (
                                <p className="text-xs text-muted-foreground">
                                  {client.brandName}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge
                              variant={
                                client.isActive ? "default" : "secondary"
                              }
                            >
                              {client.isActive ? t("Active") : t("Inactive")}
                            </Badge>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => navigate(`/users/${client._id}`)}
                            >
                              {t("View")}
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>{t("No clients assigned")}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Packages Tab */}
        <TabsContent value="packages" className="space-y-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Package className="h-5 w-5" />
                {t("Recent Packages")}
              </CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/packages")}
              >
                {t("View All")}
              </Button>
            </CardHeader>
            <CardContent>
              {overviewLoading ? (
                <div className="flex items-center justify-center py-8">
                  <ThreeDotLoader />
                </div>
              ) : recentPackages.length > 0 ? (
                <div className="space-y-3">
                  {recentPackages.slice(0, 5).map((pkg) => (
                    <Card
                      key={pkg._id}
                      className="border-l-4 border-l-blue-500 cursor-pointer hover:bg-muted/50"
                      onClick={() => navigate(`/packages/${pkg._id}`)}
                    >
                      <CardContent className="p-3">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <Package className="h-4 w-4 text-muted-foreground" />
                              <span className="font-medium text-sm">
                                {pkg.trackingNumber || pkg.code}
                              </span>
                              <Badge variant="outline" className="text-xs">
                                {pkg.status}
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground">
                              {pkg.customer?.name || pkg.customerName} •{" "}
                              {pkg.customer?.phone || pkg.customerPhone}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {pkg.customer?.city || pkg.city}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold text-sm">
                              {pkg.totalAmount || pkg.price} MAD
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>{t("No packages found")}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Pickups Tab */}
        <TabsContent value="pickups" className="space-y-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Truck className="h-5 w-5" />
                {t("Recent Pickups")}
              </CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/pickups")}
              >
                {t("View All")}
              </Button>
            </CardHeader>
            <CardContent>
              {pickupsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <ThreeDotLoader />
                </div>
              ) : pickups.length > 0 ? (
                <div className="space-y-3">
                  {pickups.slice(0, 5).map((pickup) => (
                    <Card
                      key={pickup._id}
                      className="border-l-4 border-l-indigo-500 cursor-pointer hover:bg-muted/50"
                      onClick={() => navigate(`/pickups/${pickup._id}`)}
                    >
                      <CardContent className="p-3">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <Truck className="h-4 w-4 text-muted-foreground" />
                              <span className="font-medium text-sm">
                                {pickup.name}
                              </span>
                              <Badge variant="outline" className="text-xs">
                                {pickup.status}
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground">
                              {pickup.phone} • {pickup.pickupCity}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {pickup.address}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-muted-foreground">
                              {pickup.type === "product"
                                ? t("Product")
                                : t("Package")}
                            </p>
                            {pickup.quantity && (
                              <p className="text-sm font-medium">
                                Qty: {pickup.quantity}
                              </p>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Truck className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>{t("No pickups found")}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tickets Tab */}
        <TabsContent value="tickets" className="space-y-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                {t("Recent Tickets")}
              </CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/tickets")}
              >
                {t("View All")}
              </Button>
            </CardHeader>
            <CardContent>
              {ticketsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <ThreeDotLoader />
                </div>
              ) : tickets.length > 0 ? (
                <div className="space-y-3">
                  {tickets.slice(0, 5).map((ticket) => (
                    <Card
                      key={ticket._id}
                      className="border-l-4 border-l-orange-500 cursor-pointer hover:bg-muted/50"
                      onClick={() => navigate(`/tickets/${ticket._id}`)}
                    >
                      <CardContent className="p-3">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <FileText className="h-4 w-4 text-muted-foreground" />
                              <span className="font-medium text-sm">
                                {ticket.title}
                              </span>
                            </div>
                            <p className="text-xs text-muted-foreground line-clamp-1">
                              {ticket.description}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge
                              variant={
                                ticket.status === "open"
                                  ? "secondary"
                                  : "default"
                              }
                            >
                              {ticket.status}
                            </Badge>
                            <Badge variant="outline" className="text-xs">
                              {ticket.priority}
                            </Badge>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>{t("No tickets found")}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Return Vouchers Tab */}
        <TabsContent value="returnVouchers" className="space-y-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Receipt className="h-5 w-5" />
                {t("Recent Return Vouchers")}
              </CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/return-voucher")}
              >
                {t("View All")}
              </Button>
            </CardHeader>
            <CardContent>
              {returnVouchersLoading ? (
                <div className="flex items-center justify-center py-8">
                  <ThreeDotLoader />
                </div>
              ) : returnVouchers.length > 0 ? (
                <div className="space-y-3">
                  {returnVouchers.slice(0, 5).map((voucher) => (
                    <Card
                      key={voucher._id}
                      className="border-l-4 border-l-purple-500 cursor-pointer hover:bg-muted/50"
                      onClick={() => navigate(`/return-voucher/${voucher._id}`)}
                    >
                      <CardContent className="p-3">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <Receipt className="h-4 w-4 text-muted-foreground" />
                              <span className="font-medium text-sm">
                                {voucher.client?.fullName ||
                                  voucher.client?.name}
                              </span>
                              <Badge variant="outline" className="text-xs">
                                {voucher.status}
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground">
                              {voucher.packages?.length || 0} {t("packages")} •{" "}
                              {voucher.totalAmount} MAD
                            </p>
                            {voucher.bonDeRetour && (
                              <p className="text-xs text-muted-foreground">
                                {voucher.bonDeRetour}
                              </p>
                            )}
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-medium text-green-600">
                              {voucher.totalNet} MAD
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {t("Net")}
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Receipt className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>{t("No return vouchers found")}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
